import { useCallback, useEffect, useRef } from "react"
import { z } from "zod"

import { defaultAiChatWelcomeMessage } from "@productlane/lib/src/ai-chat-defaults"
import { useZodForm } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { CollapsiblePromptTextarea } from "@/components/collapsible-prompt-textarea"
import { toast } from "@/components/toast"
import { docsDefaultExampleQuestions } from "@/constants"
import { captureError } from "@/lib/capture-error"
import { generateId } from "@/lib/id"
import { useAutosaveText } from "@/lib/use-autosave-text"
import {
	mutateOk,
	reportServerRejection,
	useZero,
	useZeroWorkspace,
} from "@/lib/zero"
import { SettingsInset } from "../settings-switches/settings-inset"
import { SettingsSection } from "../settings-switches/settings-section"
import { LearnWritingStyleButton } from "./learn-writing-style-button"

const FormSchema = z.object({
	aiDraftsCustomPrompt: z.string().optional(),
})

export function AiCustomPromptSection() {
	const workspace = useZeroWorkspace()
	const zero = useZero()

	const form = useZodForm({
		schema: FormSchema,
		defaultValues: {
			aiDraftsCustomPrompt:
				workspace?.aiChatSettings?.aiDraftsCustomPrompt ?? "",
		},
	})

	const initializedRef = useRef(false)
	useEffect(() => {
		if (!workspace?.aiChatSettings) return
		if (initializedRef.current) return
		initializedRef.current = true
		form.reset({
			aiDraftsCustomPrompt: workspace.aiChatSettings.aiDraftsCustomPrompt ?? "",
		})
	}, [workspace, form])

	const createdSettingsIdRef = useRef<string | null>(null)
	const persisted = workspace?.aiChatSettings?.aiDraftsCustomPrompt ?? ""
	const watchedPrompt = form.watch("aiDraftsCustomPrompt") ?? ""

	const reportRejected = useCallback(
		(_message: string, error: Error) => {
			captureError(error, {
				domain: "ai-chat",
				action: "ai drafts custom prompt save rejected by server",
				workspaceId: workspace?.id,
			})
			toast.error({
				title: "Your custom prompt didn't save",
				description:
					"Reload the page to get the newest version, then apply your change again.",
			})
		},
		[workspace?.id],
	)

	const save = useCallback(
		async (value: string) => {
			if (!workspace) return
			const next = value.trim().length > 0 ? value : null
			// Repeated saves must reuse the row the first one created.
			const settingsId =
				workspace.aiChatSettings?.id ?? createdSettingsIdRef.current
			try {
				if (!settingsId) {
					const id = generateId()
					createdSettingsIdRef.current = id
					const welcomeMessage = defaultAiChatWelcomeMessage(workspace.name)
					await mutateOk(
						reportServerRejection(
							zero.mutate(
								mutators.aiChatSettings.create({
									id,
									workspaceId: workspace.id,
									welcomeMessage,
									exampleQuestions: docsDefaultExampleQuestions,
									isEnabled: false,
									aiDraftsCustomPrompt: next,
								}),
							),
							reportRejected,
						),
					)
				} else {
					await mutateOk(
						reportServerRejection(
							zero.mutate(
								mutators.aiChatSettings.update({
									id: settingsId,
									aiDraftsCustomPrompt: next,
								}),
							),
							reportRejected,
						),
					)
				}
			} catch (error) {
				captureError(error, {
					domain: "ai-chat",
					action: "update ai drafts custom prompt",
					workspaceId: workspace.id,
				})
				// Keep the text in the field; a rollback would throw away the writing.
				toast.error({
					title: "Your custom prompt didn't save",
					description:
						error instanceof Error ? error.message : "An error occurred",
				})
			}
		},
		[workspace, zero, reportRejected],
	)

	// Only the user's own edits autosave, so a late-hydrating relation can never
	// overwrite a stored prompt with the empty default.
	const flushPrompt = useAutosaveText({
		value: watchedPrompt,
		persistedValue: persisted,
		onSave: save,
		enabled: !!form.formState.dirtyFields.aiDraftsCustomPrompt,
	})

	if (!workspace) return null

	return (
		<>
			<SettingsSection
				title="Custom prompt"
				description="Save instructions and writing preferences for the assistant and reply drafts. They apply across conversations until you change them."
			/>

			<div className="mt-4 flex flex-col gap-1.5">
				<SettingsInset className="flex flex-col gap-1.5">
					<CollapsiblePromptTextarea
						variant="settings"
						{...form.register("aiDraftsCustomPrompt")}
						placeholder="e.g. Keep replies friendly and under 100 words. Lead with what you confirmed, then list next steps."
						minRows={4}
						onBlur={(e) => {
							form.setValue("aiDraftsCustomPrompt", e.target.value)
							flushPrompt(e.target.value)
						}}
					/>
					<LearnWritingStyleButton
						onApply={(style) => {
							const value = [
								form.getValues("aiDraftsCustomPrompt")?.trim(),
								style,
							]
								.filter(Boolean)
								.join("\n\n")
							form.setValue("aiDraftsCustomPrompt", value, {
								shouldDirty: true,
							})
							flushPrompt(value)
						}}
					/>
				</SettingsInset>
				<p className="text-[11.5px] leading-relaxed text-gray-500 dark:text-gray-400">
					Drafts sign off with the first name of whichever teammate opens them,
					so the same draft is correct no matter who writes it.
				</p>
			</div>
		</>
	)
}
